import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { ShareModal } from '../components/ShareModal';
import { useUserOrg } from '../lib/org';
import { useCurrentSite } from '../lib/currentSite';
import {
  listOrgDocuments,
  listSiteDocuments,
  type DocumentRow,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';
import { getShareUrl } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { DOC_CATEGORIES, type DocCategory } from '../data/docTemplates';
import { LoadingIndicator } from '../components/LoadingIndicator';

/**
 * Documents issued — flat list of every document that's been generated.
 *
 * Lives on its own page (route: /documents/issued) so the template-picker
 * at /documents can stay focused on the action of issuing new docs.
 *
 * Excludes templates that are "not started" or "doesn't apply" — only
 * documents with a real file_path. Email button on each row opens the
 * ShareModal. Tap a row to open the document detail page.
 */

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isExpired(doc: DocumentRow): boolean {
  if (doc.status === 'expired') return true;
  if (doc.expires_at && new Date(doc.expires_at) < new Date()) return true;
  return false;
}

export function DocumentsIssued() {
  const { org } = useUserOrg();
  const { currentSite } = useCurrentSite();
  const [orgDocs, setOrgDocs] = useState<DocumentRow[]>([]);
  const [siteDocs, setSiteDocs] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'org' | 'site'>('all');
  const [categoryFilter, setCategoryFilter] = useState<DocCategory | 'all'>('all');
  const [shareTarget, setShareTarget] = useState<DocumentRow | null>(null);
  const [senderName, setSenderName] = useState<string>('');

  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    // Cache-first paint. Documents are cached per-org in IndexedDB by
    // the offline-support layer; split into org-scope and site-scope
    // here for the filter pills to work the same as the network path.
    cacheListByOrg<DocumentRow>('documents', org.org_id).then((cached) => {
      if (cancelled || cached.length === 0) return;
      haveCache = true;
      setOrgDocs(cached.filter((d) => !d.site_id));
      setSiteDocs(currentSite ? cached.filter((d) => d.site_id === currentSite.id) : []);
      setLoading(false);
    }).catch(() => {});
    Promise.all([
      listOrgDocuments(org.org_id),
      currentSite ? listSiteDocuments(currentSite.id) : Promise.resolve([]),
    ])
      .then(([o, s]) => { if (!cancelled) { setOrgDocs(o); setSiteDocs(s); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
  }, [org, currentSite?.id]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      setSenderName(name);
    });
  }, []);

  // Combine + filter — only docs with an actual file (excludes "not started"
  // templates that never appear in the DB at all, and "not_applicable" rows
  // that don't have a file_path).
  const allDocs = useMemo(() => {
    const combined: DocumentRow[] = [];
    if (scopeFilter === 'all' || scopeFilter === 'org') combined.push(...orgDocs);
    if (scopeFilter === 'all' || scopeFilter === 'site') combined.push(...siteDocs);
    return combined
      .filter((d) => !!d.file_path && d.status !== 'not_applicable')
      .filter((d) => categoryFilter === 'all' || d.category === categoryFilter)
      .sort((a, b) => {
        const at = a.issued_at ?? a.created_at ?? '';
        const bt = b.issued_at ?? b.created_at ?? '';
        return bt.localeCompare(at);
      });
  }, [orgDocs, siteDocs, scopeFilter, categoryFilter]);

  if (loading) {
    return <AppShell title="Issued documents" back="/documents"><LoadingIndicator /></AppShell>;
  }

  return (
    <AppShell title="Issued documents" back="/documents">
      {error && <div className="banner banner-error">{error}</div>}

      {(orgDocs.length + siteDocs.length) === 0 ? (
        <div className="card" style={{ textAlign: 'center', marginTop: 12 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No documents issued yet. Tap "‹ Back" to fill in a template.
          </p>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
              Every document you&apos;ve issued — tap a row to view, or <strong>📨 Email</strong> to send a 30-day download link.
            </p>
          </div>

          {/* Scope filter */}
          <div className="rams-trade-chips" role="tablist" aria-label="Filter by scope" style={{ marginBottom: 8 }}>
            <button type="button" className={`rams-trade-chip ${scopeFilter === 'all' ? 'active' : ''}`} onClick={() => setScopeFilter('all')}>
              All
            </button>
            <button type="button" className={`rams-trade-chip ${scopeFilter === 'org' ? 'active' : ''}`} onClick={() => setScopeFilter('org')}>
              Org-wide
            </button>
            <button type="button" className={`rams-trade-chip ${scopeFilter === 'site' ? 'active' : ''}`} onClick={() => setScopeFilter('site')} disabled={!currentSite}>
              {currentSite ? `${currentSite.name}` : 'This site (no site set)'}
            </button>
          </div>

          {/* Category filter */}
          <div className="rams-trade-chips" role="tablist" aria-label="Filter by category" style={{ marginBottom: 12 }}>
            <button type="button" className={`rams-trade-chip ${categoryFilter === 'all' ? 'active' : ''}`} onClick={() => setCategoryFilter('all')}>
              All categories
            </button>
            {DOC_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`rams-trade-chip ${categoryFilter === c.id ? 'active' : ''}`}
                onClick={() => setCategoryFilter(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {allDocs.length === 0 ? (
            <div className="card" style={{ textAlign: 'center' }}>
              <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                Nothing matches these filters — try widening with "All".
              </p>
            </div>
          ) : (
            <ul className="rams-list">
              {allDocs.map((d) => (
                <li key={d.id} className="rams-row">
                  <Link to={`/documents/${d.id}`} className="rams-row-tap" style={{ textDecoration: 'none' }}>
                    <div className="rams-row-text">
                      <p className="rams-title">
                        {d.title}
                        {isExpired(d) && (
                          <span className="status-pill status-danger" style={{ marginLeft: 8 }}>Expired</span>
                        )}
                      </p>
                      <p className="rams-meta">
                        v{d.version}
                        {' · '}{d.scope === 'org' ? 'Org-wide' : 'Site'}
                        {' · '}{d.category}
                        {' · '}Issued {fmtDate(d.issued_at ?? d.created_at)}
                        {d.expires_at && ` · expires ${fmtDate(d.expires_at)}`}
                        {d.size_bytes ? ` · ${fmtSize(d.size_bytes)}` : ''}
                      </p>
                    </div>
                    <span className="chev">›</span>
                  </Link>
                  <div className="rams-row-actions">
                    <button
                      type="button"
                      className="btn-tiny"
                      onClick={() => setShareTarget(d)}
                      disabled={!d.file_path}
                      aria-label={`Email ${d.title}`}
                      title="Email this document — sends a branded email with a 30-day download link"
                    >
                      📨 Email
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
        <Link to="/documents" className="btn btn-secondary" style={{ flex: 1 }}>
          ‹ Back to template menu
        </Link>
      </div>

      {shareTarget && shareTarget.file_path && (
        <ShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          docTitle={shareTarget.title}
          docType={shareTarget.category === 'cdm' ? 'CPP' : 'Document'}
          orgName={org?.org_name ?? undefined}
          senderName={senderName || undefined}
          getShareUrl={() => {
            if (!shareTarget.file_path) throw new Error('No file attached to this document.');
            return getShareUrl('documents', shareTarget.file_path, 30 * 24 * 60 * 60);
          }}
        />
      )}
    </AppShell>
  );
}
